import { createEvent, createStore, restore, sample } from 'effector'
import { createGate } from 'effector-react'
import { fx } from 'src/libs'
import { buildQuery } from 'src/pages/worldCup2026/utils'
import { ListRequest } from 'src/pages/worldCup2026/types'
import { BASE_URL, PAGE_SIZE } from 'src/pages/worldCup2026/const'
import {
  AddCoinsRequestData,
  BanRequest,
  BanRequestData,
  Participant,
  ParticipantListResponse,
} from './types'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'

export const ParticipantListGate = createGate()

export const onChangeParticipantList = createEvent<ListRequest>()
export const submitBanClient = createEvent<BanRequestData>()
export const submitAddCoins = createEvent<AddCoinsRequestData>()

export const getParticipantsFx = fx<ListRequest, ParticipantListResponse>({
  method: 'GET',
  url: `${BASE_URL}participant`,
})

export const banClientFx = fx<BanRequest, void | any>({
  method: 'PUT',
})

export const addCoinsFx = fx<{ amount: number }, void | any>({
  method: 'POST',
})

export const $participants = createStore<Participant[]>([]).on(
  getParticipantsFx.doneData,
  (_, res) => res.body.items,
)

export const $totalCountOfParticipants = restore(
  getParticipantsFx.doneData.map((a) => a.body.total),
  0,
)

sample({
  clock: ParticipantListGate.open,
  fn: () => ({
    query: { page: '1', limit: String(PAGE_SIZE), order: '-allTickets' },
  }),
  target: getParticipantsFx,
})

sample({
  clock: onChangeParticipantList,
  fn: buildQuery,
  target: getParticipantsFx,
})

sample({
  clock: submitAddCoins,
  fn: ({ id, amount }) => ({
    url: `${BASE_URL}participant/${id}/transfer`,
    body: { amount },
    id,
  }),
  target: addCoinsFx,
})

sample({
  clock: submitBanClient,
  fn: ({ id, banReason }) => ({
    url: `${BASE_URL}participant/${id}`,
    body: { banReason },
    id,
  }),
  target: banClientFx,
})

sample({
  clock: banClientFx.done,
  source: $participants,
  fn: (participants, { params }) => {
    const { id, body } = params as {
      id: number
      url: string
      body: { banReason: string }
    }
    return participants.map((item) => {
      return item.client.clientId === Number(id)
        ? {
            ...item,
            bannedAt: new Date().toISOString(),
            banReason: body.banReason,
          }
        : item
    })
  },
  target: $participants,
})

sample({
  clock: addCoinsFx.done,
  source: $participants,
  fn: (participants, { params }) => {
    const { id, body } = params as {
      id: number
      url: string
      body: { amount: number }
    }
    return participants.map((item) => {
      return item.client.clientId === id
        ? {
            ...item,
            allPoints: item.allPoints + body.amount,
            sumManualTransfers: String(
              Number(item.sumManualTransfers) + body.amount,
            ),
          }
        : item
    })
  },
  target: $participants,
})

sample({
  clock: [
    getParticipantsFx.failData,
    banClientFx.failData,
    addCoinsFx.failData,
  ],
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'Error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})

sample({
  clock: banClientFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Client disqualified',
  }),
  target: showNotification,
})

sample({
  clock: addCoinsFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Coins have been successfully added.',
  }),
  target: showNotification,
})
